import React, { isValidElement, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { List as AntList, Flex, Spin } from "antd";
import Localization from "../../localization";
import styled from "styled-components";
import { SVGs } from "../../assets";
import { Tools } from "../../utils";
import Text from "../text";
import Icon from "../icon";

const Styled = styled(AntList)`
  overflow: hidden;
  border-radius: 1.5rem;
  background-color: ${(_) => _.theme.token.surface.primary};
  .ant-list-item {
    min-height: 6.7rem;
    flex-wrap: unset;
    cursor: ${(_) => (_.highlightOnHover ? "pointer" : "normal")};
    padding: 1.5rem 2rem;
    .image {
      height: 4rem;
    }
    .icon {
      font-size: 3em;
      color: ${(_) => _.theme.token.colorPrimary};
    }
    ${({ highlightOnHover, theme }) =>
      highlightOnHover &&
      `
      &:hover {
        border-color: ${theme.token.colorPrimary};
        background: ${theme.token.colorPrimary};
        transition: 0.5s ease;
        .ant-typography {
          color: ${theme.token.fontColors.tertiary} !important;
        }
      }
    `}
  }
`;

const StyledPagination = styled.div`
  gap: 1rem;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${(_) => _.theme.token.colorPrimary};
  ${(_) => _.disabled && `opacity: 0.7;  pointer-events: none;`}
  .icon {
    color: ${(_) => _.theme.token.colorPrimary};
  }
`;

const Pagination = ({
  size = 0,
  total = 0,
  onShowMore = () => {},
  onShowLess = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const showLess = size >= total;

  const onClick = async () => {
    setLoading(true);
    try {
      await (showLess ? onShowLess() : onShowMore());
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPagination disabled={loading} onClick={onClick}>
      <Text
        weight={"600"}
        children={
          showLess
            ? Localization.general.showLess
            : Localization.general.showMore
        }
      />
      {loading ? (
        <Spin indicator={<LoadingOutlined spin />} size="small" />
      ) : (
        <Icon
          className="icon"
          rotate={showLess ? 180 : 0}
          component={SVGs.downOutlinedComp}
        />
      )}
    </StyledPagination>
  );
};

const List = ({ highlightOnHover = true, pagination, className, ...props }) => {
  return (
    <div className={className}>
      <Styled
        itemLayout="horizontal"
        highlightOnHover={highlightOnHover}
        {...props}
      />
      {!!pagination && pagination.total > 5 && (
        <Flex className="m-12-t" justify="center">
          <Pagination {...pagination} size={props.dataSource.length} />
        </Flex>
      )}
    </div>
  );
};

const Item = ({ image, disabled, style, ...props }) => {
  return (
    <Styled.Item
      disabled={disabled}
      extra={!!image && <img className="image" src={image} />}
      style={{
        ...(disabled && { pointerEvents: "none", cursor: "not-allowed" }),
        ...style,
      }}
      {...props}
    />
  );
};

const Meta = ({ disabled, title, description, avatar, className }) => {
  return (
    <Flex gap={"1.2rem"} align="center" className={className}>
      {avatar}
      <div>
        {!!title &&
          (!["string", "object"].includes(typeof title) ||
          isValidElement(title) ? (
            title
          ) : (
            <Text
              level={5}
              weight={"700"}
              type={disabled ? "disabledDark" : "primary"}
              {...(Tools.isObject(title) ? title : { children: title })}
            />
          ))}
        {!!description &&
          (React.isValidElement(description) ? (
            description
          ) : (
            <Text
              weight="400"
              className="m-5-t"
              children={description}
              type={disabled ? "disabledDark" : "secondary"}
            />
          ))}
      </div>
    </Flex>
  );
};

List.Item = Item;
List.Item.Meta = Meta;
export default List;
